import { store } from '@/context/store';
import { IMandatorySupportingDoc } from '@/partners/partnerTypes';

export type TRequiredDocuments = { [code: string]: string };

/**
 * Merge a brand's mandatory supporting documents on top of the selected
 * lender's required documents.
 *
 * The lender's list comes first so its ordering and wording are preserved — on
 * a code collision the lender's label wins and the brand entry is dropped,
 * because the lender's wording is what the lender's own ops team expects.
 *
 * Returns undefined when there is nothing to collect at all, so existing
 * `if (!requiredDocs)` checks keep meaning "no documents needed".
 */
export function mergeRequiredDocuments(
    lenderDocs: TRequiredDocuments | undefined,
    brandDocs: IMandatorySupportingDoc[] | undefined
): TRequiredDocuments | undefined {
    const merged: TRequiredDocuments = { ...(lenderDocs ?? {}) };

    for (const doc of brandDocs ?? []) {
        if (!doc?.code) continue;
        if (merged[doc.code]) continue;
        merged[doc.code] = doc.label || doc.code;
    }

    if (Object.keys(merged).length === 0) return undefined;

    return merged;
}

/**
 * Store-reading convenience wrapper. Prefer `mergeRequiredDocuments` with
 * `useAppSelector` inputs inside components so they re-render on change.
 */
export function getRequiredDocuments(): TRequiredDocuments | undefined {
    const state = store.getState();

    return mergeRequiredDocuments(
        state.borrowerReducer.selectedLender?.requiredDocuments,
        state.displayReducer.partner.mandatorySupportingDocuments
    );
}
